import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import type { OrganizationAdmin, Page } from "../../api/types";
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  FormError,
  Loading,
  Modal,
  PageHead,
  Pagination,
  TextField,
  useToast,
} from "../../components/ui";
import { formatDate } from "../../lib/format";
import { useAsync } from "../../lib/useAsync";
import { t } from "../../i18n";

function slugify(name: string): string {
  return name
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

export function OrganizationsPage() {
  const toast = useToast();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  useEffect(() => {
    const tp = setTimeout(() => {
      setDebounced(q);
      setPage(1);
    }, 300);
    return () => clearTimeout(tp);
  }, [q]);
  const list = useAsync(() => api.get<Page<OrganizationAdmin>>("/admin/organizations", { q: debounced, page, page_size: 20 }), [debounced, page]);

  async function toggle(o: OrganizationAdmin) {
    const next = o.status === "active" ? "suspended" : "active";
    if (next === "suspended" && !window.confirm(t("Suspender {name}? Gestores, treinadores e atletas perdem o acesso até a reativação.", { name: o.name }))) return;
    try {
      await api.patch(`/admin/organizations/${o.id}`, { status: next });
      toast(next === "suspended" ? t("{name} suspensa.", { name: o.name }) : t("{name} reativada.", { name: o.name }));
      list.reload();
    } catch (err) {
      toast((err as ApiError).message, "error");
    }
  }

  return (
    <>
      <PageHead
        title={t("Assessorias")}
        subtitle={t("Cada assessoria tem seus dados isolados. O gestor indicado recebe um convite por e-mail.")}
        actions={<Button variant="primary" onClick={() => setCreating(true)}>{t("Criar assessoria")}</Button>}
      />
      <div className="toolbar" role="search">
        <div className="grow">
          <TextField label={t("Buscar")} type="search" placeholder={t("Nome ou identificador")} value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>
      {list.loading && !list.data && <Loading />}
      {list.error && <ErrorState error={list.error} onRetry={list.reload} />}
      {list.data && list.data.total === 0 && (
        <div className="panel">
          <EmptyState title={debounced ? t("Nenhuma assessoria encontrada") : t("Nenhuma assessoria cadastrada")}
            action={!debounced && <Button variant="primary" onClick={() => setCreating(true)}>{t("Criar assessoria")}</Button>} />
        </div>
      )}
      {list.data && list.data.total > 0 && (
        <>
          <div className="table-wrap">
            <table className="data responsive">
              <thead>
                <tr>
                  <th>{t("Assessoria")}</th>
                  <th>{t("Gestão")}</th>
                  <th className="right">{t("Atletas")}</th>
                  <th className="right">{t("Treinadores")}</th>
                  <th>{t("Criada em")}</th>
                  <th>{t("Situação")}</th>
                  <th className="right">{t("Ações")}</th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((o) => (
                  <tr key={o.id}>
                    <td>
                      <strong>{o.name}</strong>
                      <span className="sub">
                        {t("{slug}, plano {plan}", { slug: o.slug, plan: o.plan })}
                      </span>
                    </td>
                    <td data-label={t("Gestão")}>{o.managers.join(", ") || "—"}</td>
                    <td data-label={t("Atletas")} className="right num">{o.athletes_count}</td>
                    <td data-label={t("Treinadores")} className="right num">{o.coaches_count}</td>
                    <td data-label={t("Criada em")}>{formatDate(o.created_at)}</td>
                    <td>{o.status === "active" ? <Badge tone="ok">{t("Ativa")}</Badge> : <Badge tone="danger">{t("Suspensa")}</Badge>}</td>
                    <td className="right">
                      <Button size="sm" variant={o.status === "active" ? "danger" : "secondary"} onClick={() => toggle(o)}>
                        {o.status === "active" ? t("Suspender") : t("Reativar")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageSize={20} total={list.data.total} onPage={setPage} />
        </>
      )}
      <CreateOrgModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(name) => {
          setCreating(false);
          toast(t("{name} criada. O convite foi enviado ao gestor.", { name }));
          list.reload();
        }}
      />
    </>
  );
}

function CreateOrgModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (name: string) => void }) {
  const empty = { name: "", slug: "", plan: "trial", manager_name: "", manager_email: "" };
  const [form, setForm] = useState(empty);
  const [slugTouched, setSlugTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  useEffect(() => {
    if (open) {
      setForm(empty);
      setSlugTouched(false);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/admin/organizations", form);
      onCreated(form.name);
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  }
  const f = error?.fieldErrors() ?? {};
  return (
    <Modal
      open={open}
      title={t("Criar assessoria")}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancelar")}
          </Button>
          <Button variant="primary" type="submit" form="org-form" busy={busy}>
            {t("Criar e convidar gestor")}
          </Button>
        </>
      }
    >
      <form id="org-form" className="form" onSubmit={submit} noValidate>
        {error && error.code !== "validation_error" && <FormError error={error} />}
        <TextField
          label={t("Nome da assessoria")}
          value={form.name}
          autoFocus
          onChange={(e) => setForm({ ...form, name: e.target.value, slug: slugTouched ? form.slug : slugify(e.target.value) })}
          error={f.name}
        />
        <div className="form-grid">
          <TextField
            label={t("Identificador")}
            hint={t("Letras minúsculas, números e hífen.")}
            value={form.slug}
            onChange={(e) => {
              setSlugTouched(true);
              setForm({ ...form, slug: e.target.value });
            }}
            error={f.slug}
          />
          <TextField label={t("Plano")} value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} error={f.plan} />
        </div>
        <h3>{t("Gestor responsável")}</h3>
        <div className="form-grid">
          <TextField label={t("Nome")} value={form.manager_name} onChange={(e) => setForm({ ...form, manager_name: e.target.value })} error={f.manager_name} />
          <TextField label={t("E-mail")} type="email" value={form.manager_email} onChange={(e) => setForm({ ...form, manager_email: e.target.value })} error={f.manager_email} />
        </div>
      </form>
    </Modal>
  );
}
